import { useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Upload, Download, ArrowLeft, CheckCircle2, AlertTriangle, Users } from "lucide-react";
import { useValidateImport, useCommitImport } from "../hooks";
import { downloadTemplate } from "../api";
import { GREEN } from "../constants";
import { Toast } from "../components/Toast";
import { Stat } from "../components/Stat";
import { StatusBadge } from "../components/StatusBadge";
import { Dropzone } from "../components/Dropzone";
import type { Merchant, ImportPreview, ImportResult } from "../types";

// Step 3 (file mode) — upload an XLSX of clients, preview the dry-run, then commit.
export function FileImportStep({
  merchant,
  onBack,
  onReset,
  onResultChange,
}: {
  merchant: Merchant;
  onBack: () => void;
  onReset: () => void;
  // Lifts the "has a committed result" flag so the wizard stepper can advance to the result step.
  onResultChange?: (hasResult: boolean) => void;
}) {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showErrorsOnly, setShowErrorsOnly] = useState(false);

  const { mutate: validate, isPending: isValidating } = useValidateImport();
  const { mutate: commit, isPending: isCommitting } = useCommitImport();

  // Keep the wizard stepper in sync with whether a result screen is showing.
  useEffect(() => {
    onResultChange?.(result !== null);
  }, [result, onResultChange]);

  const handleFile = useCallback(
    (file: File) => {
      setSelectedFile(file);
      setPreview(null);
      setShowErrorsOnly(false);
      validate({ merchantId: merchant.id, file }, { onSuccess: (data) => setPreview(data) });
    },
    [merchant.id, validate],
  );

  const handleClearFile = useCallback(() => {
    setSelectedFile(null);
    setPreview(null);
    setShowErrorsOnly(false);
  }, []);

  const handleCommit = useCallback(() => {
    if (!selectedFile) return;
    commit(
      { merchantId: merchant.id, file: selectedFile },
      {
        onSuccess: (data) => {
          setResult(data);
          setToast(t("import.fileStep.importedToast", { count: data.imported_count }));
        },
      },
    );
  }, [merchant.id, selectedFile, commit, t]);

  const handleDownload = useCallback(async () => {
    setIsDownloading(true);
    try {
      await downloadTemplate();
    } finally {
      setIsDownloading(false);
    }
  }, []);

  const visibleRows = useMemo(
    () =>
      preview
        ? showErrorsOnly
          ? preview.rows.filter((r) => r.status === "error")
          : preview.rows
        : [],
    [preview, showErrorsOnly],
  );

  // Result summary screen
  if (result) {
    return (
      <div className="mx-auto max-w-xl">
        <div className="rounded-card border border-grey-200 bg-white p-5 text-center sm:p-8">
          <CheckCircle2 size={48} color={GREEN} strokeWidth={1.5} className="mx-auto mb-4" />
          <h3 className="mb-4 text-[18px] font-semibold text-grey-800">
            {t("import.fileStep.resultTitle")}
          </h3>
          <div className="mb-6 grid grid-cols-3 gap-3 text-center">
            <Stat label={t("import.fileStep.imported")} value={result.imported_count} color="green" />
            <Stat label={t("import.fileStep.skipped")} value={result.skipped_exists} color="amber" />
            <Stat label={t("import.fileStep.errors")} value={result.error_count} color="red" />
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={onReset}
              className="rounded-control bg-green px-6 py-2.5 text-[14px] font-semibold text-white transition hover:bg-green-dark active:scale-95"
            >
              {t("import.fileStep.newImport")}
            </button>
            <Link
              to="/customers"
              className="flex items-center justify-center gap-2 rounded-control border border-grey-200 px-6 py-2.5 text-[14px] font-medium text-grey-700 transition-colors hover:bg-grey-50"
            >
              <Users size={16} strokeWidth={1.8} />
              {t("import.fileStep.viewCustomers")}
            </Link>
          </div>
        </div>
        {toast && <Toast message={toast} onClose={() => setToast(null)} />}
      </div>
    );
  }

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={onBack}
        className="mb-4 flex items-center gap-1.5 text-[13px] text-grey-500 hover:text-grey-700"
      >
        <ArrowLeft size={14} strokeWidth={2} />
        {t("import.back")}
      </button>

      <div className="mb-5 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-[18px] font-semibold text-grey-800">{t("import.fileStep.title")}</h2>
          <p className="text-[13px] text-grey-500">
            {merchant.first_name} {merchant.last_name}
          </p>
        </div>
        <button
          type="button"
          onClick={handleDownload}
          disabled={isDownloading}
          className="flex shrink-0 items-center gap-2 rounded-control border border-grey-200 bg-white px-4 py-2 text-[13px] font-medium text-grey-700 transition-colors hover:bg-grey-50 disabled:opacity-60"
        >
          <Download size={15} strokeWidth={1.8} />
          {isDownloading ? t("app.loading") : t("import.fileStep.downloadTemplate")}
        </button>
      </div>

      <Dropzone
        file={selectedFile}
        onFile={handleFile}
        onClear={handleClearFile}
        disabled={isValidating || isCommitting}
      />

      {/* Validation skeleton */}
      {isValidating && (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-16 animate-pulse rounded-card bg-grey-100" />
            ))}
          </div>
          <div className="h-48 animate-pulse rounded-card bg-grey-100" />
        </div>
      )}

      {/* Preview */}
      {preview && !isValidating && (
        <>
          {/* Summary counts */}
          <div className="mb-4 grid grid-cols-3 gap-3">
            <Stat label={t("import.fileStep.readyCount")} value={preview.ready_count} color="green" />
            <Stat label={t("import.fileStep.existsCount")} value={preview.exists_count} color="amber" />
            <Stat label={t("import.fileStep.errorCount")} value={preview.error_count} color="red" />
          </div>

          {/* All-error guard — nothing will be imported. */}
          {preview.ready_count === 0 && (
            <div className="mb-4 flex items-start gap-2 rounded-card border border-warning-border bg-warning-bg px-4 py-3 text-[13px] text-warning">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" strokeWidth={2} />
              <span>{t("import.fileStep.noReady")}</span>
            </div>
          )}

          {/* "Only errors" filter — shown only when there are errors to filter to. */}
          {preview.error_count > 0 && (
            <div className="mb-2 flex justify-end">
              <button
                type="button"
                onClick={() => setShowErrorsOnly((v) => !v)}
                aria-pressed={showErrorsOnly}
                className={`flex items-center gap-1.5 rounded-pill border px-3 py-1 text-[12px] font-medium transition-colors ${
                  showErrorsOnly
                    ? "border-red bg-red-bg text-red"
                    : "border-grey-200 text-grey-600 hover:bg-grey-50"
                }`}
              >
                <AlertTriangle size={13} strokeWidth={2} />
                {t("import.fileStep.onlyErrors", { count: preview.error_count })}
              </button>
            </div>
          )}

          {/* Row table — sticky header, scrollable body */}
          <div className="mb-5 max-h-[440px] overflow-auto rounded-card border border-grey-200">
            <table className="min-w-full text-[13px]">
              <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_#e5e7eb]">
                <tr>
                  <th className="px-4 py-2.5 text-left font-semibold text-grey-500">
                    {t("import.fileStep.colRow")}
                  </th>
                  <th className="px-4 py-2.5 text-left font-semibold text-grey-500">
                    {t("import.fileStep.colName")}
                  </th>
                  <th className="px-4 py-2.5 text-left font-semibold text-grey-500">
                    {t("import.fileStep.colPhone")}
                  </th>
                  <th className="px-4 py-2.5 text-left font-semibold text-grey-500">
                    {t("import.fileStep.colStatus")}
                  </th>
                  <th className="px-4 py-2.5 text-left font-semibold text-grey-500">
                    {t("import.fileStep.colNote")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-grey-100 bg-white">
                {visibleRows.map((row) => {
                  const bg =
                    row.status === "ready"
                      ? "bg-green-bg"
                      : row.status === "exists"
                        ? "bg-warning-bg"
                        : "bg-red-bg";
                  return (
                    <tr key={row.row} className={bg}>
                      <td className="px-4 py-2.5 text-grey-500 tnum">{row.row}</td>
                      <td className="px-4 py-2.5 text-grey-800">
                        {row.first_name} {row.last_name}
                      </td>
                      <td className="px-4 py-2.5 text-grey-700 tnum">{row.phone_number}</td>
                      <td className="px-4 py-2.5">
                        <StatusBadge status={row.status} t={t} />
                      </td>
                      <td className="px-4 py-2.5 text-[12px]">
                        {row.status === "exists" && row.existing && (
                          <span className="text-warning">
                            {t("import.fileStep.existsHint", {
                              field:
                                row.matched_field === "pin"
                                  ? t("import.fileStep.fieldPin")
                                  : t("import.fileStep.fieldPhone"),
                              name: `${row.existing.first_name} ${row.existing.last_name}`,
                            })}
                          </span>
                        )}
                        {row.status === "error" && row.message && (
                          <span className="text-red">{row.message}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onBack}
              className="rounded-control border border-grey-200 px-5 py-2.5 text-[14px] font-medium text-grey-700 hover:bg-grey-50"
            >
              {t("import.back")}
            </button>
            <button
              type="button"
              onClick={handleCommit}
              disabled={isCommitting || preview.ready_count === 0}
              className="flex items-center gap-2 rounded-control bg-green px-5 py-2.5 text-[14px] font-semibold text-white transition hover:bg-green-dark active:scale-95 disabled:opacity-60 disabled:active:scale-100"
            >
              <Upload size={16} strokeWidth={2} />
              {isCommitting
                ? t("app.loading")
                : t("import.fileStep.commitBtn", { count: preview.ready_count })}
            </button>
          </div>
        </>
      )}

      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </div>
  );
}
